import { createContext, useContext, useMemo, useCallback } from "react";
import { UserType } from "shared/types/enum";
import { getAllStaffByBusinessId } from "hooks/queries/staff";
import {
  CALENDAR_COLOR_KEYS,
  calendarPalette,
  CalendarColor,
  CalendarColorKey,
  colors,
} from "shared/theme/color";

type InstructorColorContextType = {
  getInstructorColorById: (instructorId: string) => CalendarColor | undefined;
};

const InstructorColorContext = createContext<InstructorColorContextType>({
  getInstructorColorById: () => undefined,
});

const isCalendarColorKey = (key: unknown): key is CalendarColorKey =>
  typeof key === "string" &&
  (CALENDAR_COLOR_KEYS as readonly string[]).includes(key);

// A colour for an instructor the server hasn't given one yet (a row created by
// a script). Hashed from the id so it is the same on every page load, never
// picked by position in the list, which the server returns in no fixed order.
export const fallbackCalendarColorKey = (
  instructorId: string
): CalendarColorKey => {
  let hash = 0;
  for (let i = 0; i < instructorId.length; i++) {
    hash = (hash * 31 + instructorId.charCodeAt(i)) | 0;
  }
  return CALENDAR_COLOR_KEYS[Math.abs(hash) % CALENDAR_COLOR_KEYS.length];
};

export const calendarColorKeyOf = (
  instructorId: string,
  savedKey?: string | null
): CalendarColorKey =>
  isCalendarColorKey(savedKey)
    ? savedKey
    : fallbackCalendarColorKey(instructorId);

export const InstructorColorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: instructors } = getAllStaffByBusinessId(UserType.Instructor, {
    is_active: true,
  });

  // Each instructor's saved colour (staff.calendar_color, picked by the server
  // when they became an instructor and changeable on their profile), shared by
  // the calendar blocks, the list-view avatars, payroll and the sider filter.
  const instructorColorMap = useMemo(() => {
    const map = new Map<string, CalendarColor>();
    (instructors || []).forEach((instructor) => {
      map.set(
        instructor.id,
        calendarPalette[
          calendarColorKeyOf(instructor.id, instructor.staff?.calendar_color)
        ]
      );
    });
    return map;
  }, [instructors]);

  // Once the active list has loaded, anyone not on it (a deactivated
  // instructor, or one whose instructor role was removed) gets the inactive
  // grey. Nothing is coloured while the list is still loading, so nobody
  // flashes grey first.
  const getInstructorColorById = useCallback(
    (instructorId: string): CalendarColor | undefined => {
      if (!instructorId || !instructors) return undefined;
      return instructorColorMap.get(instructorId) ?? colors.calendarInactive;
    },
    [instructors, instructorColorMap]
  );

  const contextValue = useMemo(
    () => ({
      getInstructorColorById,
    }),
    [getInstructorColorById]
  );

  return (
    <InstructorColorContext.Provider value={contextValue}>
      {children}
    </InstructorColorContext.Provider>
  );
};

export const useInstructorColor = () => {
  const context = useContext(InstructorColorContext);
  if (!context) {
    throw new Error(
      "useInstructorColor must be used within the InstructorColorProvider"
    );
  }
  return context;
};
